import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Search } from "lucide-react";
import { cn } from "../../utils/cn";
import useHelpdeskLabels from "./useHelpdeskLabels";
import { labelColorStyles } from "./LabelChip";

// ─── Selector de etiquetas de una conversación ───────────────────────────────
// Popover multi-selección con búsqueda. Cada clic aplica el cambio en el acto
// (como Chatwoot): no hay botón "Guardar" que olvidar. El estado "pendiente"
// por etiqueta evita doble clic mientras el PATCH viaja.

/**
 * @param {object}   props
 * @param {string[]} props.selectedIds         etiquetas actuales de la conversación
 * @param {(id:string, add:boolean) => void} props.onToggle
 * @param {string|null} [props.pendingId]      etiqueta cuyo cambio está en vuelo
 * @param {() => void} props.onClose
 * @param {object}   [props.anchorRef]         botón que abre (no cuenta como click fuera)
 */
export default function LabelPicker({ selectedIds = [], onToggle, pendingId = null, onClose, anchorRef }) {
  const { t } = useTranslation();
  const { labels, loading, error, reload } = useHelpdeskLabels();
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  // Orden fijado al abrir: si se reordenara con cada clic, la fila saltaría
  // bajo el puntero.
  const [initiallySelected] = useState(() => new Set(selectedIds));

  useEffect(() => {
    const handleClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose, anchorRef]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? labels.filter((l) => l.name.toLowerCase().includes(q)) : labels;
    // Las aplicadas primero: es lo que el agente suele venir a quitar.
    return [...list].sort(
      (a, b) => Number(initiallySelected.has(b.id)) - Number(initiallySelected.has(a.id)),
    );
  }, [labels, query, initiallySelected]);

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={t("Etiquetas de la conversación")}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose?.();
          // Con teclado, el foco vuelve al botón que abrió el panel.
          anchorRef?.current?.focus?.();
        }
      }}
      className="absolute left-0 top-full mt-1 z-30 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-bg shadow-elevated animate-scale-in origin-top-left motion-reduce:animate-none"
    >
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted pointer-events-none"
            aria-hidden="true"
          />
          <input
            // type="search", not "text": the legacy global rule
            // `input[type="text"]` (_modern-forms.scss) overrides pl-8 and the
            // placeholder ran under the icon. The native clear button is hidden;
            // max-md:text-[16px] keeps the 16px that rule gave phones (no iOS zoom).
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Buscar etiqueta…")}
            aria-label={t("Buscar etiqueta")}
            className="w-full h-8 pl-8 pr-3 rounded-md border border-border bg-bg text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500 max-md:text-[16px] [&::-webkit-search-cancel-button]:appearance-none"
          />
        </div>
      </div>

      <div
        role="listbox"
        aria-multiselectable="true"
        aria-label={t("Etiquetas")}
        className="max-h-60 overflow-y-auto py-1"
      >
        {loading ? (
          <div className="px-3 py-2 space-y-2" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-bg-muted animate-pulse" />
                <div className="h-3 w-2/3 rounded bg-bg-muted animate-pulse" />
              </div>
            ))}
          </div>
        ) : error && labels.length === 0 ? (
          <div className="px-3 py-4 text-center">
            <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t("No se pudieron cargar las etiquetas.")}
            </div>
            <button
              type="button"
              onClick={() => reload()}
              className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
            >
              {t("Reintentar")}
            </button>
          </div>
        ) : labels.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-fg-muted">
            {t("Aún no hay etiquetas. Un administrador o supervisor puede crearlas en los ajustes del helpdesk.")}
          </div>
        ) : visible.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-fg-muted">
            {t("Sin resultados para la búsqueda.")}
          </div>
        ) : (
          visible.map((label) => {
            const isOn = selected.has(label.id);
            const busy = pendingId === label.id;
            return (
              <button
                key={label.id}
                type="button"
                role="option"
                aria-selected={isOn}
                disabled={busy}
                onClick={() => onToggle?.(label.id, !isOn)}
                className={cn(
                  "w-full min-h-[36px] flex items-center gap-2 px-3 py-1.5 text-left text-[13px] focus:outline-none focus-visible:bg-bg-muted disabled:opacity-60 transition-colors",
                  isOn ? "text-fg font-medium" : "text-fg hover:bg-bg-muted",
                )}
              >
                <span
                  className={cn(
                    "inline-flex w-4 h-4 items-center justify-center rounded border shrink-0",
                    isOn ? "bg-brand-500 border-brand-500 text-bg hd-brand-solid" : "border-border bg-bg",
                  )}
                  aria-hidden="true"
                >
                  {isOn && <Check className="w-3 h-3" />}
                </span>
                <span
                  className={cn("w-2 h-2 rounded-full shrink-0", labelColorStyles(label.color).dot)}
                  aria-hidden="true"
                />
                <span className="flex-1 min-w-0 truncate">{label.name}</span>
                {busy && (
                  <span
                    className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none text-fg-muted"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
